import { memo, useCallback, useEffect, useMemo } from "react";
import {
  Tldraw,
  DefaultStylePanel,
  type Editor,
  type TLPresenceUserInfo,
  type TLStore,
} from "tldraw";
import { useSync } from "@tldraw/sync";
import { CanvasStylePanel } from "@/canvas/components/CanvasPanels";
import {
  CANVAS_COMPONENTS,
  CANVAS_OPTIONS,
  CANVAS_UI_OVERRIDES,
} from "@/canvas/canvasOptions";
import { isInSyncRemove } from "../spaces-diff-filter";
import { SLIDE_EMBED_TYPE } from "../shapes/SlideEmbedShapeUtil";
import {
  SPACES_SHAPE_UTILS,
  SPACES_COMPONENTS_BASE,
  noopAssets,
} from "../constants";
import { usePersonalSyncStore } from "../hooks/usePersonalSyncStore";
import { buildTeacherShapeStampHandler } from "../spacesCanvasUtils";
import {
  SHARED_CHANNEL,
  type SpaceSyncMultiplexer,
} from "../spaceSyncMultiplexer";
import type { TldrawCameraVector } from "../spacesPresenceUtils";
import { TLDRAW_LICENSE_KEY } from "@/constants/tldraw";
import { TldrawDrawToolRegion } from "@/drawlock/TldrawDrawToolRegion";
import { ET_CANVAS_ASSET_URLS } from "@ever-tutor/et-ui";

/**
 * Thin wrapper that exposes the RemoteTLStoreWithStatus to the parent via
 * onStoreReady — needed so the teacher's shared store can be injected into
 * the StudentWork canvas as backdrop content.
 */
export const MuxSpaceCanvas = memo(function MuxSpaceCanvas({
  mux,
  userInfo,
  shouldPublishPresence,
  onMount,
  onSynced,
  onStoreReady,
  stylePanel,
}: {
  mux: SpaceSyncMultiplexer;
  userInfo: TLPresenceUserInfo | undefined;
  // When false, useSync stops publishing this client's instance_presence record
  // — and tldraw emits a remove for it, which clears any stale cursor on peers
  // (otherwise the teacher's last-known cursor lingers in the shared room while
  // they're actually drawing on a student's personal canvas).
  shouldPublishPresence: boolean;
  onMount?: (editor: Editor) => void;
  onSynced?: () => void;
  onStoreReady?: (store: ReturnType<typeof useSync>) => void;
  stylePanel: typeof DefaultStylePanel | typeof CanvasStylePanel;
}) {
  const connect = useCallback(() => mux.getChannel(SHARED_CHANNEL), [mux]);
  // Force-suppress presence by returning null from getUserPresence; tldraw will
  // emit a remove for our existing record so peers stop seeing a stale cursor.
  // When shouldPublishPresence=true we omit the option entirely so useSync uses
  // its own default presence derivation.
  const suppressedPresence = useCallback(() => null, []);
  const store = useSync({
    connect,
    assets: noopAssets,
    userInfo,
    ...(shouldPublishPresence ? {} : { getUserPresence: suppressedPresence }),
    shapeUtils: SPACES_SHAPE_UTILS,
  });
  const components = useMemo(
    () => ({ ...SPACES_COMPONENTS_BASE, StylePanel: stylePanel }),
    [stylePanel],
  );
  useEffect(() => {
    onStoreReady?.(store);
  }, [store, onStoreReady]);
  useEffect(() => {
    if (store.status === "synced-remote") {
      onSynced?.();
    }
  }, [store.status, onSynced]);

  return (
    <div className="tldraw-canvas">
      <Tldraw
        licenseKey={TLDRAW_LICENSE_KEY}
        assetUrls={ET_CANVAS_ASSET_URLS}
        store={store}
        shapeUtils={SPACES_SHAPE_UTILS}
        options={CANVAS_OPTIONS}
        overrides={CANVAS_UI_OVERRIDES}
        components={components}
        onMount={onMount}
      >
        <TldrawDrawToolRegion />
      </Tldraw>
    </div>
  );
});

/**
 * Student Work canvas — re-mounted (via parent React key) for each watched student.
 *
 * The teacher's editor binds DIRECTLY to the student's personal sync store. This
 * is what makes presence work natively (useSync derives + publishes the teacher's
 * instance_presence into the personal room — students see the live cursor without
 * any manual mirroring).
 *
 * Shared/teacher content (slides, anything in the shared room) is injected into
 * personalStore LOCALLY via mergeRemoteChanges so the teacher sees it as backdrop —
 * those records never round-trip through the personal channel.
 *
 * The teacher operates here under the same constraints a student has in
 * personal-edit mode: their own user-created shapes are routed to personalStore
 * (meta.layer === watchedStudentId), and shared/teacher shapes are read-only.
 */
export const StudentWorkCanvas = memo(function StudentWorkCanvas({
  mux,
  watchedStudentId,
  teacherUserId,
  teacherUserInfo,
  sharedStore,
  components,
  initialCamera,
  liveCamera,
}: {
  mux: SpaceSyncMultiplexer;
  watchedStudentId: string;
  teacherUserId: number;
  teacherUserInfo: TLPresenceUserInfo | undefined;
  sharedStore: TLStore | null;
  components: typeof SPACES_COMPONENTS_BASE;
  initialCamera: TldrawCameraVector | null;
  liveCamera: TldrawCameraVector | null;
}) {
  const studentWorkSyncStore = usePersonalSyncStore(
    mux,
    watchedStudentId,
    teacherUserInfo,
  );
  const onLoadingMount = useCallback((editor: Editor) => {
    editor.setCameraOptions({ zoomSteps: [1] });
    editor.updateInstanceState({ isGridMode: true });
  }, []);

  // Inject from sharedStore into personalStore LOCALLY so the teacher sees:
  //   • shape / asset / binding   — slides + any teacher shapes as backdrop
  //   • instance_presence (student) — the student's live cursor/camera, which
  //     they actually publish into the SHARED room (their editor is bound to
  //     sharedStore). Without mirroring it here, personalStore would have no
  //     student presence and the teacher would see no cursor.
  // Wrapped in mergeRemoteChanges → personal useSync treats these as remote
  // updates and does NOT broadcast them back to the personal room.
  // instance/camera/pointer/page-state are intentionally NOT injected — those
  // are owned by the teacher's own editor on this store.
  const isInjectableFromShared = useCallback(
    (r: { typeName?: string; userId?: string } | null | undefined) => {
      if (!r) return false;
      if (
        r.typeName === "shape" ||
        r.typeName === "asset" ||
        r.typeName === "binding"
      )
        return true;
      if (r.typeName === "instance_presence" && r.userId === watchedStudentId)
        return true;
      return false;
    },
    [watchedStudentId],
  );

  useEffect(() => {
    if (!studentWorkSyncStore || !sharedStore) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type StoreChanges = Parameters<
      Parameters<typeof sharedStore.listen>[0]
    >[0]["changes"];

    const applyChanges = (changes: StoreChanges) => {
      const added = Object.values(changes.added).filter(isInjectableFromShared);
      const updated = Object.values(changes.updated)
        .map(([, next]) => next)
        .filter(isInjectableFromShared);
      const removedIds = Object.entries(changes.removed)
        .filter(([, r]) => isInjectableFromShared(r as any))
        .map(([id]) => id);
      if (!added.length && !updated.length && !removedIds.length) return;
      studentWorkSyncStore.mergeRemoteChanges(() => {
        if (added.length) studentWorkSyncStore.put(added);
        if (updated.length) studentWorkSyncStore.put(updated);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (removedIds.length) studentWorkSyncStore.remove(removedIds as any);
      });
    };

    // Subscribe BEFORE reading the snapshot. Any change that lands between
    // subscribe and the end of the initial put is buffered into `queue` and
    // replayed in arrival order after the snapshot is in place. Once
    // `queue === null` the listener switches to direct apply (live mode).
    let queue: StoreChanges[] | null = [];
    const unsubscribe = sharedStore.listen(({ changes }) => {
      if (queue !== null) queue.push(changes);
      else applyChanges(changes);
    });

    studentWorkSyncStore.mergeRemoteChanges(() => {
      studentWorkSyncStore.put(
        sharedStore.allRecords().filter(isInjectableFromShared),
      );
    });

    const buffered = queue;
    queue = null;
    for (const changes of buffered) applyChanges(changes);

    return () => unsubscribe();
  }, [sharedStore, studentWorkSyncStore, isInjectableFromShared]);

  const onMount = useCallback(
    (editor: Editor) => {
      editor.setCameraOptions({ zoomSteps: [1] });
      editor.updateInstanceState({ isGridMode: true });
      // The teacher comes to a student's board to mark it up: pen in hand.
      editor.setCurrentTool("draw");
      // Stamp teacher-authored shapes with meta.layer = watchedStudentId. Since
      // editor.store IS the personal sync store, useSync will broadcast these
      // shapes into the student's personal room natively — no diff-filter needed.
      editor.sideEffects.registerBeforeCreateHandler(
        "shape",
        buildTeacherShapeStampHandler(teacherUserId, watchedStudentId),
      );

      // Read-only guard for shared/teacher content: any user attempt to mutate a
      // shape that doesn't belong to this student's personal layer is rejected.
      // Slides and other teacher-room shapes stay frozen as backdrop.
      editor.sideEffects.registerBeforeChangeHandler(
        "shape",
        (prev, next, source) => {
          if (source !== "user") return next;
          const meta = (next.meta ?? {}) as Record<string, unknown>;
          if (meta.layer === watchedStudentId) return next;
          return prev;
        },
      );
      editor.sideEffects.registerBeforeDeleteHandler("shape", (shape) => {
        // Allow sync-originated removes: studentWorkSyncStore is a raw TLStore (not
        // patched by spaces-diff-filter), so isMergingRemoteChanges is the reliable
        // signal that this remove came from mergeRemoteChanges in the sharedStore
        // listener or the personal room listener — not from a user action.
        const syncStore = editor.store as any;
        if (isInSyncRemove() || syncStore.isMergingRemoteChanges === true)
          return;
        const meta = (shape.meta ?? {}) as Record<string, unknown>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((shape as any).type === SLIDE_EMBED_TYPE) return false;
        if (meta.layer !== watchedStudentId) return false;
      });

      // Land on the student's last-known viewport so the teacher opens on the
      // same content the student is looking at. After this initial set, the
      // teacher pans/zooms freely — we deliberately do NOT follow live camera
      // updates so watching doesn't hijack the teacher's viewport.
      const cam = liveCamera ?? initialCamera;
      if (cam) editor.setCamera(cam, { animation: { duration: 0 } });
    },
    [watchedStudentId, teacherUserId, initialCamera, liveCamera],
  );

  // While the personal sync store is connecting, render a placeholder Tldraw so
  // the canvas slot doesn't flash empty (and keeps the same `tldraw-canvas`
  // wrapper that the rest of the layout depends on).
  if (!studentWorkSyncStore) {
    return (
      <div className="tldraw-canvas">
        <Tldraw
          licenseKey={TLDRAW_LICENSE_KEY}
          assetUrls={ET_CANVAS_ASSET_URLS}
          key="loading"
          components={CANVAS_COMPONENTS}
          shapeUtils={SPACES_SHAPE_UTILS}
          options={CANVAS_OPTIONS}
          overrides={CANVAS_UI_OVERRIDES}
          onMount={onLoadingMount}
        />
      </div>
    );
  }

  return (
    <div className="tldraw-canvas">
      <Tldraw
        licenseKey={TLDRAW_LICENSE_KEY}
        assetUrls={ET_CANVAS_ASSET_URLS}
        store={studentWorkSyncStore}
        shapeUtils={SPACES_SHAPE_UTILS}
        options={CANVAS_OPTIONS}
        overrides={CANVAS_UI_OVERRIDES}
        components={components}
        onMount={onMount}
      >
        <TldrawDrawToolRegion />
      </Tldraw>
    </div>
  );
});
